import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import FadeInImage from '../../../shared/components/FadeInImage';

/**
 * Premium product-page gallery.
 *
 *   - 1 image  → single full-width hero
 *   - 2 images → side by side
 *   - 3+ images → big hero (left) + 2-up stack (right), with a "show all
 *     photos" button that opens a full-screen lightbox.
 *
 * `images` is an array of { id, pathImage, name }.
 */
export default function EventGallery({ images = [], title }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const pics = (images || []).filter((p) => p && p.pathImage);
  if (!pics.length) return null;

  const openAt = (i) => {
    setIndex(i);
    setOpen(true);
  };
  const prev = () => setIndex((i) => (i - 1 + pics.length) % pics.length);
  const next = () => setIndex((i) => (i + 1) % pics.length);

  const single = pics.length === 1;
  const duo = pics.length === 2;

  return (
    <>
      <div className="relative">
        <div
          className={
            single
              ? 'overflow-hidden rounded-2xl'
              : 'grid h-[300px] grid-cols-1 gap-2 overflow-hidden rounded-2xl sm:h-[380px] sm:grid-cols-2 lg:h-[440px]'
          }
        >
          {/* Hero image */}
          <button
            type="button"
            onClick={() => openAt(0)}
            className={`group relative overflow-hidden ${single ? '' : duo ? '' : 'sm:row-span-2'}`}
          >
            <FadeInImage
              src={pics[0].pathImage}
              alt={title}
              eager
              className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
            />
            <span className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/5" />
          </button>

          {/* Side images (only when 2+) */}
          {!single &&
            pics.slice(1, duo ? 2 : 3).map((p, i) => (
              <button
                key={p.id}
                type="button"
                onClick={() => openAt(i + 1)}
                className="group relative hidden overflow-hidden sm:block"
              >
                <FadeInImage
                  src={p.pathImage}
                  alt={p.name || title}
                  className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                />
                <span className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/5" />
              </button>
            ))}
        </div>

        {/* Show-all button */}
        {pics.length > 1 && (
          <button
            type="button"
            onClick={() => openAt(0)}
            className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-sm font-semibold text-ink shadow-card backdrop-blur transition-transform hover:scale-105 rtl:right-auto rtl:left-4"
          >
            <Squares2X2Icon className="h-4 w-4" />
            {t('detail.showAllPhotos')} · {pics.length}
          </button>
        )}
      </div>

      {/* Lightbox */}
      <Transition show={open} as={Fragment}>
        <Dialog onClose={() => setOpen(false)} className="relative z-[60]">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-ink/90" />
          </Transition.Child>

          <div className="fixed inset-0 flex flex-col">
            <div className="flex items-center justify-between px-4 py-4 text-white/90">
              <span className="text-sm font-medium">
                {index + 1} / {pics.length}
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="relative flex flex-1 items-center justify-center px-4 pb-8">
              <button
                type="button"
                onClick={prev}
                aria-label="Previous"
                className="absolute left-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
              >
                <ChevronLeftIcon className="h-6 w-6 rtl:rotate-180" />
              </button>

              <img
                src={pics[index].pathImage}
                alt={pics[index].name || title}
                className="max-h-full max-w-full rounded-xl object-contain"
              />

              <button
                type="button"
                onClick={next}
                aria-label="Next"
                className="absolute right-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
              >
                <ChevronRightIcon className="h-6 w-6 rtl:rotate-180" />
              </button>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
